import { useEffect, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { CategoryPicker } from './CategoryPicker.jsx';
import { useCashboxes } from '../../../cashbox/hooks/useCashboxes.js';
import { useCreateExpense } from '../../hooks/useExpenses.js';
import { toast } from '../../../../shared/useToast.js';

const METHODS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'transfer', label: 'تحويل' },
  { key: 'external', label: 'خارجي' }
];

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_SIZE = 5 * 1024 * 1024;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function defaults() {
  return {
    categoryId: '',
    amount: 0,
    date: todayISO(),
    paymentMethod: 'cash',
    cashboxId: '',
    description: '',
    supplierName: '',
    notes: ''
  };
}

export function ExpenseForm({ open, onClose, onSuccess }) {
  const { data: cashboxes = [] } = useCashboxes();
  const create = useCreateExpense();
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: defaults() });

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (open) {
      reset(defaults());
      setFile(null);
      setPreviewUrl(null);
    }
  }, [open, reset]);

  const method = watch('paymentMethod');

  const cashboxOptions = useMemo(
    () => cashboxes.filter((c) => c.isActive !== false),
    [cashboxes]
  );

  if (!open) return null;

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      setPreviewUrl(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error('نوع الملف غير مدعوم (JPG/PNG/WEBP/PDF فقط)');
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error('حجم الملف يتجاوز 5 ميجا');
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(f));
    } else {
      setPreviewUrl(null);
    }
  }

  async function submit(values) {
    if (!values.categoryId) return;
    if (!values.description?.trim()) return;
    if (values.paymentMethod === 'cash' && !values.cashboxId) return;
    const amount = Number(values.amount);
    if (!amount || amount <= 0) return;

    const body = {
      categoryId: values.categoryId,
      amount,
      date: values.date || todayISO(),
      paymentMethod: values.paymentMethod,
      description: values.description.trim(),
      // الخادم يسمّي الحقل supplierName — إرسال supplier كان يُحذف بصمت.
      supplierName: values.supplierName?.trim() || '',
      notes: values.notes?.trim() || ''
    };
    if (values.paymentMethod === 'cash' && values.cashboxId) {
      body.cashboxId = values.cashboxId;
    }

    await create.mutateAsync(
      { body, attachment: file },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose?.();
        }
      }
    );
  }

  // النافذة: درج بارتفاع الشاشة، تذييله لاصق وجسمه هو ما يُمرَّر.
  // كانت الحاوية `grid place-items-stretch` بصفٍّ تلقائي، والصف يتمدّد بطول
  // المحتوى — فيصير `h-full` على النموذج = ارتفاع المحتوى لا ارتفاع النافذة،
  // فلا يتقلّص الجسم ولا يعمل `overflow-y-auto`، ويهبط زرّ الإنشاء تحت حدّ
  // الشاشة بلا شريط تمرير (لا يُرى إلا بتصغير تكبير المتصفح). `flex` لا
  // يتمدّد بالمحتوى، و`min-h-0` يُلغي الحدّ الأدنى التلقائي لعنصر الـ flex
  // الذي كان يمنع تقلّص الجسم.
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch overflow-hidden"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="bg-white border-l border-line shadow-card flex flex-col mr-auto h-full max-h-full min-h-0 overflow-hidden"
        style={{ width: 520, maxWidth: '92vw' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h shrink-0">
          <h3 className="text-[15px] font-bold flex-1">مصروف جديد</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3 overflow-y-auto flex-1 min-h-0">
          <Controller
            control={control}
            name="categoryId"
            rules={{ required: true }}
            render={({ field }) => (
              <CategoryPicker
                value={field.value}
                onChange={field.onChange}
                error={errors.categoryId}
              />
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ (د.ع) *</label>
              <input
                className={clsx('input', errors.amount && 'border-red')}
                type="number"
                min="0"
                step="100"
                {...register('amount', { valueAsNumber: true, required: true, min: 1 })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">التاريخ</label>
              <input
                className="input"
                type="date"
                {...register('date')}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع</label>
            <Controller
              control={control}
              name="paymentMethod"
              render={({ field }) => (
                <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
                  {METHODS.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => {
                        field.onChange(m.key);
                        if (m.key !== 'cash') setValue('cashboxId', '');
                      }}
                      className={clsx(
                        'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                        field.value === m.key
                          ? 'bg-orange text-white shadow-orange-glow'
                          : 'text-ink-2 hover:bg-white'
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {method === 'cash' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة *</label>
              <select
                className={clsx('input', errors.cashboxId && 'border-red')}
                {...register('cashboxId', { required: method === 'cash' })}
              >
                <option value="">— اختر —</option>
                {cashboxOptions.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الوصف *</label>
            <input
              className={clsx('input', errors.description && 'border-red')}
              placeholder="ما هو الغرض من هذا المصروف؟"
              {...register('description', { required: true })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المورّد</label>
            <input className="input" placeholder="اختياري" {...register('supplierName')} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المرفق (فاتورة، إيصال)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileChange}
              className="block w-full text-[12px]"
            />
            <div className="text-[11px] text-ink-3 mt-1">JPG / PNG / WEBP / PDF — حد أقصى 5 ميجا</div>
            {file ? (
              <div className="mt-2 p-2.5 rounded-[10px] bg-paper-2 flex items-center gap-2.5">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="w-12 h-12 object-cover rounded-[8px]" />
                ) : (
                  <div className="w-12 h-12 grid place-items-center bg-white rounded-[8px] text-[20px]">📄</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold truncate">{file.name}</div>
                  <div className="text-[11px] text-ink-3">{(file.size / 1024).toFixed(0)} كيلوبايت</div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setFile(null);
                    setPreviewUrl(null);
                  }}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
            <textarea className="input" rows={2} {...register('notes')} />
          </div>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2 shrink-0 bg-white">
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'جاري...' : 'تسجيل المصروف'}
          </button>
        </div>
      </form>
    </div>
  );
}
